import { useCollection } from "@/shared/domain/collections/hooks/useCollection";
import {
  ConfirmationAlert,
  ConfirmationCard,
  ConfirmationChips,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationNote,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";

import type { ConfirmationContentProps } from "./ClientTools";
import {
  NEUTRAL_RESOURCE_NOUN,
  RESOURCE_TYPE_NOUNS,
  countLabel,
  pluralize,
  describeId,
  findTypeMismatches,
} from "./collectionResourceIds";
import {
  coerceAddToCollectionInput,
  type AddToCollectionParams,
} from "./collectionToolInput";

/**
 * Read-only preview of a client_add_to_collection proposal.
 *
 * Rendered inline on the approval card as well as in the details modal — the
 * target collection and the IDs moving in or out are the whole of what a user
 * is being asked to approve, so they cannot sit behind a disclosure.
 *
 * Fetches the target collection (via {@link useCollection}) so the card can
 * show its name and resource_type instead of a bare `cl_` ID — a failed or
 * still-loading fetch degrades to showing the raw ID rather than blocking
 * the preview, since the fetch is a display nicety, not a precondition for
 * approving. Members to add/remove are listed as bare IDs (no per-member
 * fetch): the LLM can propose dozens at once, and the ID itself is enough to
 * eyeball for "does this look right".
 *
 * A proposal `execute` would refuse renders as an alert and nothing else:
 * presenting a coherent card for a write that cannot succeed invites an
 * approval that achieves nothing.
 */
export function AddToCollectionConfirmation({
  params,
}: ConfirmationContentProps<AddToCollectionParams>) {
  // The same verdict `execute` will reach, so what is approvable is what is
  // sendable.
  const verdict = coerceAddToCollectionInput(params);
  const collectionQuery = useCollection(
    verdict.ok ? verdict.payload.collectionId : undefined,
  );

  if (!verdict.ok) {
    return (
      <ConfirmationPanel label="Edit collection preview">
        <ConfirmationAlert>
          This request won&rsquo;t succeed. {verdict.reason}
        </ConfirmationAlert>
      </ConfirmationPanel>
    );
  }

  const { collectionId, add, remove } = verdict.payload;
  const collection = collectionQuery.data;
  // `execute` refuses the whole edit when any proposed ID contradicts the
  // collection's type, so surface that here rather than presenting a coherent
  // preview of a write that cannot succeed.
  const mismatchedIds = collection
    ? findTypeMismatches([...add, ...remove], collection.resource_type)
    : [];
  // Label by the collection's type only when every proposed ID agrees with it;
  // otherwise "Add 3 datasets" would sit above a list of file IDs.
  const resourceNoun =
    collection && mismatchedIds.length === 0
      ? RESOURCE_TYPE_NOUNS[collection.resource_type]
      : NEUTRAL_RESOURCE_NOUN;
  const displayName = collectionQuery.isLoading
    ? "Loading collection…"
    : (collection?.name ?? collectionId);

  return (
    <ConfirmationPanel label="Edit collection preview">
      <ConfirmationCard
        title={displayName}
        badge={
          collection && pluralize(RESOURCE_TYPE_NOUNS[collection.resource_type])
        }
      >
        {mismatchedIds.length > 0 && collection && (
          <ConfirmationAlert>
            The request won&rsquo;t succeed: this collection holds{" "}
            {pluralize(RESOURCE_TYPE_NOUNS[collection.resource_type])}, but{" "}
            {mismatchedIds.map(describeId).join("; ")}.
          </ConfirmationAlert>
        )}

        {collectionQuery.isError && (
          <ConfirmationNote>
            Couldn&rsquo;t load this collection&rsquo;s details &mdash; showing
            the raw ID below.
          </ConfirmationNote>
        )}

        <ConfirmationFields>
          <ConfirmationField label="Collection ID">
            {collectionId}
          </ConfirmationField>

          {add.length > 0 && (
            <ConfirmationField
              label={`Add ${countLabel(add.length, resourceNoun)}`}
            >
              <ConfirmationChips items={add} />
            </ConfirmationField>
          )}

          {remove.length > 0 && (
            <ConfirmationField
              label={`Remove ${countLabel(remove.length, resourceNoun)}`}
            >
              <ConfirmationChips items={remove} />
            </ConfirmationField>
          )}
        </ConfirmationFields>
      </ConfirmationCard>
    </ConfirmationPanel>
  );
}
